/**
 * In-app notification contract, mirroring the backend notifications module.
 * Copy (`title`, `message`, `actions[].label`) arrives pre-translated in the
 * reader's language — render it as-is. The backend never sends URLs: portals
 * map `subject` to their own routes.
 */

export enum NotificationCategory {
  LEAVE_MANAGEMENT = 'LEAVE_MANAGEMENT',
  TASKS = 'TASKS',
  SURVEYS = 'SURVEYS',
  /** Performance reviews and 1:1s. */
  PERFORMANCE = 'PERFORMANCE',
  ANNOUNCEMENTS = 'ANNOUNCEMENTS',
  TIMESHEETS = 'TIMESHEETS',
  EMPLOYEE_LIFECYCLE = 'EMPLOYEE_LIFECYCLE',
  COMPENSATION_AND_BENEFITS = 'COMPENSATION_AND_BENEFITS',
  COMPANY_UPDATES = 'COMPANY_UPDATES',
  /** Employee requests to change their own records. */
  CHANGE_REQUESTS = 'CHANGE_REQUESTS',
  /** Formation / Training assignments and their reminders. */
  TRAININGS = 'TRAININGS',
  /** Outcomes of background jobs and scheduled work. No human actor. */
  SYSTEM = 'SYSTEM',
}

/** Which role a notification's copy and actions were written for. */
export enum NotificationAudience {
  /** The person the event happened to. */
  SUBJECT = 'SUBJECT',
  /** Whoever is expected to act — these carry action buttons. */
  APPROVER = 'APPROVER',
  /** Company or branch admins. Informational. */
  ADMIN = 'ADMIN',
  /** Whoever triggered a background job, receiving its outcome. */
  ACTOR = 'ACTOR',
  /** Interested party — a note's author, a task's creator. */
  WATCHER = 'WATCHER',
}

export enum NotificationType {
  MESSAGING_MESSAGE = 'MESSAGING_MESSAGE',
  // LEAVE_MANAGEMENT
  LEAVE_REQUESTED = 'LEAVE_REQUESTED',
  LEAVE_APPROVED = 'LEAVE_APPROVED',
  LEAVE_REJECTED = 'LEAVE_REJECTED',
  LEAVE_CANCELLED = 'LEAVE_CANCELLED',
  LEAVE_DELETED = 'LEAVE_DELETED',
  LEAVE_DIRECT_GRANTED = 'LEAVE_DIRECT_GRANTED',
  /** A leave year opened with days in it (leave cycles). */
  LEAVE_YEAR_STARTED = 'LEAVE_YEAR_STARTED',
  LEAVE_APPROVER_UPDATED = 'LEAVE_APPROVER_UPDATED',
  LEAVE_APPROVER_RESPONSIBILITY_ASSIGNED = 'LEAVE_APPROVER_RESPONSIBILITY_ASSIGNED',
  LEAVE_REROUTED = 'LEAVE_REROUTED',
  LEAVE_DECIDED_BY_HR = 'LEAVE_DECIDED_BY_HR',
  LEAVE_MANAGER_AWAY_NO_BACKUP = 'LEAVE_MANAGER_AWAY_NO_BACKUP',
  LEAVE_BACKUP_DIGEST = 'LEAVE_BACKUP_DIGEST',
  /** "Notify" pressed on a Needs attention line. */
  ATTENTION_PING = 'ATTENTION_PING',

  // TASKS
  TASK_ASSIGNED = 'TASK_ASSIGNED',
  TASK_SUBMITTED = 'TASK_SUBMITTED',
  TASK_COMPLETED = 'TASK_COMPLETED',
  TASK_REOPENED = 'TASK_REOPENED',
  TASK_OVERDUE = 'TASK_OVERDUE',
  /** A line of a workflow step's reminder schedule ("Rappels"). */
  TASK_REMINDER = 'TASK_REMINDER',
  TASK_MESSAGE_POSTED = 'TASK_MESSAGE_POSTED',
  /** A workflow automation's Notify action (spec 2026-10-30). */
  AUTOMATION_NOTICE = 'AUTOMATION_NOTICE',
  /** An automation cancelled a task it created (the change was removed). */
  AUTOMATION_TASK_CANCELLED = 'AUTOMATION_TASK_CANCELLED',
  /** An automation was suspended by its rate cap. */
  AUTOMATION_SUSPENDED = 'AUTOMATION_SUSPENDED',

  // SURVEYS
  SURVEY_ASSIGNED = 'SURVEY_ASSIGNED',
  SURVEY_RESPONSE_RECEIVED = 'SURVEY_RESPONSE_RECEIVED',
  SURVEY_CLOSED = 'SURVEY_CLOSED',
  SURVEY_REOPENED = 'SURVEY_REOPENED',

  // PERFORMANCE
  PERFORMANCE_REVIEW_REQUESTED = 'PERFORMANCE_REVIEW_REQUESTED',
  PERFORMANCE_REVIEW_REMINDER = 'PERFORMANCE_REVIEW_REMINDER',
  PERFORMANCE_REVIEW_SHARED = 'PERFORMANCE_REVIEW_SHARED',
  PERFORMANCE_REVIEW_ACKNOWLEDGED = 'PERFORMANCE_REVIEW_ACKNOWLEDGED',
  ONE_ON_ONE_SCHEDULED = 'ONE_ON_ONE_SCHEDULED',
  PERFORMANCE_SELF_REVIEW_SUBMITTED = 'PERFORMANCE_SELF_REVIEW_SUBMITTED',
  ONE_ON_ONE_AGENDA_REMINDER = 'ONE_ON_ONE_AGENDA_REMINDER',
  ONE_ON_ONE_SUMMARY = 'ONE_ON_ONE_SUMMARY',
  PERFORMANCE_GOAL_ASSIGNED = 'PERFORMANCE_GOAL_ASSIGNED',
  PERFORMANCE_GOAL_CHECK_IN_REMINDER = 'PERFORMANCE_GOAL_CHECK_IN_REMINDER',
  /** HR's digest of the automatic probation evaluations (company portal). */
  PERFORMANCE_PROBATION_EVALUATIONS = 'PERFORMANCE_PROBATION_EVALUATIONS',
  /** HR's digest of probation manager reviews past their due date (V2-10). */
  PERFORMANCE_PROBATION_EVALUATIONS_LATE = 'PERFORMANCE_PROBATION_EVALUATIONS_LATE',

  // RECRUITING
  /** A recruiter asks a colleague to connect their Outlook for interviews. */
  RECRUITING_CALENDAR_CONNECT_REQUESTED = 'RECRUITING_CALENDAR_CONNECT_REQUESTED',
  /** HR shared a candidate with a colleague (employee portal). */
  CANDIDATE_SHARED = 'CANDIDATE_SHARED',
  /** A colleague gave their opinion on a shared candidate (to the sharer). */
  RECRUITING_CANDIDATE_OPINION = 'RECRUITING_CANDIDATE_OPINION',

  // ANNOUNCEMENTS
  ANNOUNCEMENT_PUBLISHED = 'ANNOUNCEMENT_PUBLISHED',

  // TIMESHEETS
  TIMESHEET_WEEK_SUBMITTED = 'TIMESHEET_WEEK_SUBMITTED',
  TIMESHEET_REQUEST_SUBMITTED = 'TIMESHEET_REQUEST_SUBMITTED',
  TIMESHEET_REQUEST_APPROVED = 'TIMESHEET_REQUEST_APPROVED',
  TIMESHEET_REQUEST_REJECTED = 'TIMESHEET_REQUEST_REJECTED',
  TIMESHEET_WEEK_APPROVED = 'TIMESHEET_WEEK_APPROVED',
  TIMESHEET_WEEK_REJECTED = 'TIMESHEET_WEEK_REJECTED',
  TIMESHEET_WEEK_REOPENED = 'TIMESHEET_WEEK_REOPENED',

  // EMPLOYEE_LIFECYCLE
  EMPLOYEE_WELCOME = 'EMPLOYEE_WELCOME',
  EMPLOYEE_TERMINATED = 'EMPLOYEE_TERMINATED',
  ONBOARDING_FLOW_STARTED = 'ONBOARDING_FLOW_STARTED',
  OFFBOARDING_FLOW_STARTED = 'OFFBOARDING_FLOW_STARTED',
  FLOW_COMPLETED = 'FLOW_COMPLETED',
  PROBATION_REVIEW_DUE = 'PROBATION_REVIEW_DUE',
  /** A manager recommended a probation decision or asked HR to discuss it. */
  PROBATION_RECOMMENDATION = 'PROBATION_RECOMMENDATION',

  // COMPENSATION_AND_BENEFITS
  BONUS_AWARDED = 'BONUS_AWARDED',
  BENEFIT_ENROLLED = 'BENEFIT_ENROLLED',
  BENEFIT_UNENROLLED = 'BENEFIT_UNENROLLED',
  SALARY_REVISED = 'SALARY_REVISED',
  BANK_ACCOUNT_ADDED = 'BANK_ACCOUNT_ADDED',
  BANK_ACCOUNT_UPDATED = 'BANK_ACCOUNT_UPDATED',

  // COMPANY_UPDATES
  HOLIDAY_ANNOUNCEMENT = 'HOLIDAY_ANNOUNCEMENT',
  HOLIDAYS_BATCH_PUBLISHED = 'HOLIDAYS_BATCH_PUBLISHED',
  HOLIDAY_UPDATED = 'HOLIDAY_UPDATED',
  HOLIDAY_CANCELLED = 'HOLIDAY_CANCELLED',
  ASSET_ASSIGNED = 'ASSET_ASSIGNED',
  ASSET_REVOKED = 'ASSET_REVOKED',
  DOCUMENT_ASSIGNED = 'DOCUMENT_ASSIGNED',
  DOCUMENT_REQUESTED = 'DOCUMENT_REQUESTED',
  DOCUMENT_REQUEST_REMINDER = 'DOCUMENT_REQUEST_REMINDER',
  DOCUMENT_REQUEST_FULFILLED = 'DOCUMENT_REQUEST_FULFILLED',
  ACKNOWLEDGEMENT_REQUESTED = 'ACKNOWLEDGEMENT_REQUESTED',
  ACKNOWLEDGEMENT_REMINDER = 'ACKNOWLEDGEMENT_REMINDER',
  SIGNATURE_REQUESTED = 'SIGNATURE_REQUESTED',
  SIGNATURE_REMINDER = 'SIGNATURE_REMINDER',
  SIGNATURE_WITHDRAWN = 'SIGNATURE_WITHDRAWN',
  SIGNATURE_FILED = 'SIGNATURE_FILED',
  SIGNATURE_APPROVAL_REQUESTED = 'SIGNATURE_APPROVAL_REQUESTED',
  SIGNATURE_CHANGES_REQUESTED = 'SIGNATURE_CHANGES_REQUESTED',
  SIGNATURE_DECLINED = 'SIGNATURE_DECLINED',
  SIGNATURE_COMPLETED = 'SIGNATURE_COMPLETED',
  SIGNATURE_EXPIRED = 'SIGNATURE_EXPIRED',
  SIGNATURE_MANAGER_NOTICE = 'SIGNATURE_MANAGER_NOTICE',
  FILE_SHARED = 'FILE_SHARED',
  FOLDER_SHARED = 'FOLDER_SHARED',
  SHIFT_POLICY_UPDATED = 'SHIFT_POLICY_UPDATED',
  HR_NOTE_SHARED = 'HR_NOTE_SHARED',
  HR_NOTE_COMMENT_ADDED = 'HR_NOTE_COMMENT_ADDED',
  LEAVE_POLICY_ASSIGNED = 'LEAVE_POLICY_ASSIGNED',
  LEAVE_POLICY_UPDATED = 'LEAVE_POLICY_UPDATED',
  EMPLOYEE_NOTE_SHARED = 'EMPLOYEE_NOTE_SHARED',
  EMPLOYEE_NOTE_COMMENT_ADDED = 'EMPLOYEE_NOTE_COMMENT_ADDED',
  BRANCH_MANAGER_ASSIGNED = 'BRANCH_MANAGER_ASSIGNED',
  BRANCH_DEACTIVATED = 'BRANCH_DEACTIVATED',
  COMPANY_ADMIN_WELCOME = 'COMPANY_ADMIN_WELCOME',
  COMPANY_ADMIN_ROLE_UPDATED = 'COMPANY_ADMIN_ROLE_UPDATED',
  COMPANY_ADMIN_STATUS_UPDATED = 'COMPANY_ADMIN_STATUS_UPDATED',
  COMPANY_ADMIN_REVOKED = 'COMPANY_ADMIN_REVOKED',

  // SYSTEM
  BULK_IMPORT_COMPLETED = 'BULK_IMPORT_COMPLETED',
  BULK_IMPORT_FAILED = 'BULK_IMPORT_FAILED',
  LEAVE_ALLOCATION_COMPLETED = 'LEAVE_ALLOCATION_COMPLETED',
  LEAVE_ALLOCATION_FAILED = 'LEAVE_ALLOCATION_FAILED',
  LEAVE_CARRY_FORWARD_COMPLETED = 'LEAVE_CARRY_FORWARD_COMPLETED',
  LEAVE_CARRY_FORWARD_FAILED = 'LEAVE_CARRY_FORWARD_FAILED',
  // TRAININGS
  TRAINING_ASSIGNED = 'TRAINING_ASSIGNED',
  TRAINING_REMINDER = 'TRAINING_REMINDER',
  TRAINING_DUE_SOON = 'TRAINING_DUE_SOON',
  TRAINING_OVERDUE = 'TRAINING_OVERDUE',
  TRAINING_REPORT_OVERDUE = 'TRAINING_REPORT_OVERDUE',
  TRAINING_FAILED = 'TRAINING_FAILED',
  TRAINING_EXTERNAL_SUBMITTED = 'TRAINING_EXTERNAL_SUBMITTED',
  TRAINING_EXTERNAL_REVIEWED = 'TRAINING_EXTERNAL_REVIEWED',
}

/** What a notification is about — the portal's routing key. */
export enum NotificationSubjectType {
  MESSAGING_CONVERSATION = 'MESSAGING_CONVERSATION',
  LEAVE_REQUEST = 'LEAVE_REQUEST',
  LEAVE_POLICY = 'LEAVE_POLICY',
  LEAVE_WALLET = 'LEAVE_WALLET',
  LEAVE_GRANT = 'LEAVE_GRANT',
  LEAVE_MANAGER = 'LEAVE_MANAGER',
  TASK = 'TASK',
  TASK_OCCURRENCE = 'TASK_OCCURRENCE',
  TASK_ASSIGNMENT = 'TASK_ASSIGNMENT',
  SURVEY = 'SURVEY',
  /** id: review id. Read `context.cycleId` for the cycle. */
  PERFORMANCE_REVIEW = 'PERFORMANCE_REVIEW',
  /** id: submission id. Read `context.reviewId` / `context.reviewerType`. */
  PERFORMANCE_REVIEW_SUBMISSION = 'PERFORMANCE_REVIEW_SUBMISSION',
  /** id: 1:1 series id. Read `context.meetingId` for its first meeting. */
  ONE_ON_ONE = 'ONE_ON_ONE',
  /** id: goal id (a check-in reminder: the first stale goal). */
  PERFORMANCE_GOAL = 'PERFORMANCE_GOAL',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  TIMESHEET_REQUEST = 'TIMESHEET_REQUEST',
  TIMESHEET_WEEK = 'TIMESHEET_WEEK',
  EMPLOYEE = 'EMPLOYEE',
  EMPLOYEE_TERMINATION = 'EMPLOYEE_TERMINATION',
  EMPLOYEE_FLOW = 'EMPLOYEE_FLOW',
  EMPLOYEE_NOTE = 'EMPLOYEE_NOTE',
  HR_NOTE = 'HR_NOTE',
  EMPLOYEE_DOCUMENT = 'EMPLOYEE_DOCUMENT',
  /** HR asking an employee for a document; `context.employeeId` names them. */
  DOCUMENT_REQUEST = 'DOCUMENT_REQUEST',
  /** One person's copy of a document to read and confirm (E-signatures). */
  DOCUMENT_ACKNOWLEDGEMENT = 'DOCUMENT_ACKNOWLEDGEMENT',
  /** An e-signature request; `context.employeeId` names who signs. */
  SIGNATURE_REQUEST = 'SIGNATURE_REQUEST',
  COMPANY_FILE = 'COMPANY_FILE',
  COMPANY_FOLDER = 'COMPANY_FOLDER',
  EMPLOYEE_ASSET = 'EMPLOYEE_ASSET',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  /** id: change request id. Read `context.employeeId` / `context.section`. */
  EMPLOYEE_CHANGE_REQUEST = 'EMPLOYEE_CHANGE_REQUEST',
  SALARY = 'SALARY',
  BONUS = 'BONUS',
  BENEFIT = 'BENEFIT',
  HOLIDAY = 'HOLIDAY',
  HOLIDAY_CALENDAR = 'HOLIDAY_CALENDAR',
  SHIFT_POLICY = 'SHIFT_POLICY',
  BRANCH = 'BRANCH',
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  BULK_IMPORT_JOB = 'BULK_IMPORT_JOB',
  LEAVE_SCHEDULED_JOB = 'LEAVE_SCHEDULED_JOB',
  RECRUITING_JOB = 'RECRUITING_JOB',
  /** The reader's own Outlook connection for interviews (id = their user). */
  CALENDAR_CONNECTION = 'CALENDAR_CONNECTION',
  /** A candidate shared with the reader (id = the share). */
  CANDIDATE_SHARE = 'CANDIDATE_SHARE',
  /** A recruiting applicant (id = the application). */
  RECRUITING_APPLICATION = 'RECRUITING_APPLICATION',
  /** A training assignment; `context.trainingId` names the training. */
  TRAINING = 'TRAINING',
  /** An external training entry; `context.employeeId`. */
  TRAINING_EXTERNAL = 'TRAINING_EXTERNAL',
  AUTOMATION_RUN = 'AUTOMATION_RUN',
}

export enum NotificationActionId {
  VIEW = 'VIEW',
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  CANCEL = 'CANCEL',
  ACKNOWLEDGE = 'ACKNOWLEDGE',
  COMPLETE = 'COMPLETE',
  REVIEW_FAILURES = 'REVIEW_FAILURES',
}

export type NotificationActionIntent = 'primary' | 'danger' | 'neutral';

/** What to ask the reader for. Drives the input's label, never the body key. */
export type NotificationPromptKind = 'comment' | 'reason';

/**
 * Extra input the client must collect before firing the request. `field` names
 * the body key to send it under — the endpoints disagree (`rejectionReason` for
 * a leave rejection, `note` for a timesheet, `adminNote` for a task reopen), so
 * the descriptor has to say which. NEVER hardcode the key.
 */
export interface NotificationActionPrompt {
  kind: NotificationPromptKind;
  field: string;
}

export interface NotificationActionRequest {
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Fully-resolved API path, relative to the API root. */
  path: string;
  body?: Record<string, unknown>;
  /** Absent means no extra input. See `NotificationActionPrompt`. */
  prompt?: NotificationActionPrompt;
}

export interface NotificationAction {
  id: NotificationActionId;
  /** Already resolved for the reader's language. */
  label: string;
  /** The i18n key behind `label`, in case the portal prefers its own copy. */
  labelKey: string;
  intent: NotificationActionIntent;
  /** Absent means "navigate to the subject", with no API call. */
  request?: NotificationActionRequest;
}

export interface NotificationSubject {
  type: NotificationSubjectType;
  id: string;
  /** Tenant data, never translated — safe to render verbatim. */
  label?: string | null;
  /** Domain status, so the client can retire actions that no longer apply. */
  status?: string | null;
}

/**
 * Type-specific structured detail. Only the six fields below are present on
 * essentially every notification — treat everything else as optional.
 */
export interface NotificationContext extends Record<string, unknown> {
  resourceType?: string;
  resourceId?: string;
  resourceTitle?: string;
  /** `null` for system and scheduled work. */
  actorUserId?: string | null;
  /** Display name, or `"System"`. */
  actorName?: string;
  occurredAt?: string;
}

export interface NotificationItem {
  /** Recipient row — use for read/delete, and as the React key. */
  id: string;
  /** The shared notification; several people can hold the same one. */
  notificationId: string;
  title: string;
  /** `null` for a title-only notification. */
  message: string | null;
  type: NotificationType;
  category: NotificationCategory;
  audience: NotificationAudience;
  /** `null` on rare system notifications — fall back to `context`. */
  subject: NotificationSubject | null;
  actions: NotificationAction[];
  context: NotificationContext | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationDateGroup {
  /**
   * `YYYY-MM-DD` in the company timezone as it comes off the wire. Label it on
   * the client — which regroups the pages on the reader's own date first, so
   * the label and the date it is derived from share one clock.
   */
  date: string;
  items: NotificationItem[];
}

/**
 * Pagination is per item, not per group, so a group can straddle a page
 * boundary — merge on `date` when appending a page.
 */
export interface NotificationFeed {
  groups: NotificationDateGroup[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetNotificationsParams {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
  category?: NotificationCategory;
  /** Backs the panel's Approvals tab, which asks for `APPROVER` only. */
  audience?: NotificationAudience;
}

export interface NotificationUnreadCount {
  count: number;
}

export interface MarkNotificationReadResponse {
  success: boolean;
  isRead: boolean;
  readAt: string | null;
}

export interface MarkAllNotificationsReadResponse {
  success: boolean;
  affected: number;
}

export interface DeleteNotificationResponse {
  success: boolean;
}

/* ── SSE stream ──────────────────────────────────────────────────────────── */

export enum NotificationStreamEvent {
  Ready = 'ready',
  Notification = 'notification',
  NotificationUpdated = 'notification-updated',
  Shutdown = 'shutdown',
  SlowConsumer = 'slow-consumer',
}

export interface NotificationReadyFrame {
  unreadCount: number;
  heartbeatMs: number;
}

export interface NotificationPushFrame {
  items: NotificationItem[];
  unreadCount: number;
}

/** Why the server closed a stream. `reason` is not limited to these. */
export enum NotificationStreamEndReason {
  ServerShutdown = 'server-shutdown',
  /** A 4th connection for this user+company evicted the oldest — this one. */
  ReplacedByNewerConnection = 'replaced-by-newer-connection',
  SlowConsumer = 'buffer-exceeded',
}

export interface NotificationStreamEndFrame {
  reason: string;
}

/**
 * Mobile push device OS. Consumed by the Flutter app rather than a TS client, so this
 * exists to keep the enum declared in one place alongside the rest of the contract.
 */
export enum PushPlatform {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
}

export interface RegisterPushDeviceRequest {
  fcmToken: string;
  platform: PushPlatform;
  deviceId: string;
  locale?: string;
  appVersion?: string;
  osVersion?: string;
  deviceModel?: string;
}

/**
 * Deep-link destination a push notification points at. Coarser than
 * `NotificationSubjectType` — a shared folder and a shared file open the same
 * mobile screen — and distinct from the `type` field of the FCM payload, which
 * carries `NotificationType`.
 *
 * Consumed by the mobile app rather than a TS client, and mirrored from the
 * backend enum of the same name, like `PushPlatform` above.
 */
export enum PushEntityType {
  MESSAGING_CONVERSATION = 'messaging_conversation',
  LEAVE_REQUEST = 'leave_request',
  NOTE = 'note',
  DOCUMENT = 'document',
  SURVEY = 'survey',
  TASK = 'task',
  TASK_MESSAGE = 'task_message',
  /** A document to read and confirm; entityId = its acknowledgement id. */
  ACKNOWLEDGEMENT = 'acknowledgement',
  /** E-signature request; entityId = the request id, never a URL. */
  SIGNATURE_REQUEST = 'signature_request',
  /** A 1:1; entityId = the series, `meetingId` = the meeting. */
  ONE_ON_ONE = 'one_on_one',
  /** A goal; entityId = the goal (the first of a digest, with `count`). */
  GOAL = 'goal',
  /** A performance review (submission or review id, with `reviewId`, `reviewerType`, `count`). */
  PERFORMANCE_REVIEW = 'performance_review',
}

/**
 * The FCM `data` map. Every value is a string — FCM rejects anything else — and
 * every optional key is absent rather than empty when it could not be resolved,
 * so the app must treat a missing `entityType` as "open the notification list".
 */
export interface PushDataPayload {
  notificationId: string;
  /** `notification_recipients.id` — what the mark-as-read endpoint keys on. */
  recipientId: string;
  type: NotificationType;
  category: NotificationCategory;
  companyId: string;
  subjectType: NotificationSubjectType | '';
  subjectId: string;
  /** Absent when this notification has no mobile destination. */
  entityType?: PushEntityType;
  /** Present exactly when `entityType` is. */
  entityId?: string;
  /** `task_message` only, and only when the thread is known. */
  threadId?: string;
  /** `document` only, when the entity is a file inside a folder. */
  folderId?: string;
  /** `one_on_one` only, when the event is about one meeting. */
  meetingId?: string;
  /** `performance_review` only: the review a submission belongs to. */
  reviewId?: string;
  /** `performance_review` requests and reminders: SELF, PEER or MANAGER. */
  reviewerType?: string;
  /** Digests (`performance_review`, `goal`): how many items, as a string. */
  count?: string;
}
